import { useContext, useEffect, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { Loader } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { LoginFlowContext, useVoterUserContext } from '@nv/contexts'
import { VoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './UserLogin.module.scss'
import '@djthoms/pretty-checkbox'

const initialValues = {
  username: '',
  password: '',
}

export function UserLogin({ findMemberNumber = false, helpEmail }: any) {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const navigate = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { setVoterUser } = useVoterUserContext()

  const LOGIN_CONTEXT = useContext(LoginFlowContext)

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(t('auth.login.validations.username.voter')),
    password: Yup.string().required(t('auth.login.validations.password')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        if (!executeRecaptcha) {
          //console.log('Execute recaptcha not yet available')
          return
        }
        const token = await executeRecaptcha('voter_login')

        const userInput = {
          username: values.username,
          password: values.password,
        }
        const resp = await VoterAuthService.login(userInput.username, userInput.password, '', token)

        if (resp?.success) {
          if (resp.user) {
            setVoterUser(resp.user)
          }
          if (!resp.link) {
            if (resp.passwordResetRequired) {
              LOGIN_CONTEXT.setLoginFlow('Forgot Password')
            }
            navigate('/voting/voter-verify/select-verifier')
          }
          setError(false)
        } else {
          setError(true)
          // console.error(resp.error?.message)
          switch (resp.error?.message) {
            case 'Invalid user credentials':
              setErrorMessage(t('auth.login.errors.invalidCredentials'))
              break
            case 'session error: only one session per user':
              setErrorMessage(t('auth.login.errors.session'))
              break
            case 'Account disabled':
              setErrorMessage(t('auth.login.errors.accountDisabled'))
              break
            case 'Account locked.':
              setErrorMessage(t('auth.login.errors.accountLocked'))
              break
            case 'recaptcha error':
              setErrorMessage(t('auth.login.errors.recaptcha'))
              break
            default:
              setErrorMessage(t('auth.login.errors.generic'))
              break
          }
        }
      } catch (error) {
        setError(true)
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    if (errorMessage) {
      setErrorMessage('')
    }
  }, [formik.values.password])

  return (
    <div className={styles['container']}>
      <form
        className={styles['form']}
        onSubmit={(e) => {
          e.preventDefault()
          formik.submitForm()
        }}
      >
        <span className={styles['title']}>{t('pages.login')}</span>

        <div className={styles['form__section']}>
          <TextField
            classes={{ container: styles['username-input'] }}
            formik={formik}
            field='username'
            placeholder={t('auth.login.labels.username.voter')}
            label={t('auth.login.labels.username.voter')}
            isRequired={true}
          />

          {findMemberNumber && (
            <a
              href={
                currentLanguage === 'en'
                  ? 'https://pipsc.ca/member-tools/find-your-membership-number'
                  : 'https://pipsc.ca/fr/outils-des-membres/numero-de-membre'
              }
              className={styles['membership']}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('auth.login.labels.username.helpLinkLabel')}
            </a>
          )}
        </div>

        <div className={styles['form__section']}>
          <TextField
            classes={{ container: styles['password-input'] }}
            formik={formik}
            field='password'
            isPassword={true}
            placeholder={t('auth.login.labels.password')}
            label={t('auth.login.labels.password')}
            isRequired={true}
          />

          <div className={styles['forgot-pass']}>
            {t('auth.login.forgotPassword')}
            <NVButton
              variant='text'
              classes={{ container: styles['forgot-pass__link'] }}
              onClick={() => navigate('/voting/voter-auth/reset-password')}
              text={t('buttons.click')}
            />
          </div>
        </div>

        {error && (
          <span className={styles['errors']} role='alert'>
            {errorMessage}
          </span>
        )}

        {loading ? (
          <Loader />
        ) : (
          <NVButton
            classes={{ container: styles['button'], content: styles['content'] }}
            height={50}
            text={t('buttons.next')}
            onClick={() => formik.submitForm()}
            type='submit'
          />
        )}
      </form>
      {helpEmail && (
        <div className={styles['footer']}>
          {t('auth.help.needHelp')}{' '}
          <a target='_blank' rel='noreferrer' href={`mailto:${helpEmail}`}>
            {t('auth.help.contactUs')}
          </a>
        </div>
      )}
    </div>
  ) //
}
